import type { ApiResponse } from "../../models/v2/api";
import { ApiException } from "./errors";
import { v4 as uuidv4 } from "uuid";
import { AuthStore } from "../../store/authStore";
import { notify } from "../notifications";

/**
 * Base URL for API requests
 * Set via environment variable VITE_API_BASE_URL
 */
const getBaseUrl = (): string => {
    return import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
};

/**
 * Common auth error codes that require session cleanup and redirect
 */
const AUTH_ERROR_CODES = ['AUTH_REQUIRED', 'SESSION_EXPIRED', 'INVALID_SESSION'];

/**
 * Handle session expiry/invalid logic
 */
const handleSessionExpired = (message: string = "Session expired. Please sign in again.") => {
    console.warn("[Auth] Session expired or invalid:", message);
    AuthStore.clearSession();
    notify.error(message);

    // Force redirect to login
    // Using window.location to ensure clean state, but history.push would be SPA-friendly
    // For critical auth failures, a hard redirect is often safer.
    window.location.href = '/login';
};

/**
 * Centralized API fetch wrapper
 * 
 * Responsibilities:
 * 1. Proactive expiry check BEFORE request
 * 2. Header injection (X-Session-ID, X-Request-Id)
 * 3. Reactive error handling AFTER response
 * 4. Response unwrapping
 */
export async function apiFetch<T>(
    path: string,
    options: RequestInit = {}
): Promise<T> {
    // 1. Proactive Expiry Check
    if (AuthStore.isAuthenticated() && AuthStore.isExpired()) {
        handleSessionExpired("Your session has expired. Please sign in again.");
        // Throwing an error here to abort the request flow
        throw new Error("Session expired proactively");
    }

    const baseUrl = getBaseUrl();
    const url = `${baseUrl}${path}`;
    const requestId = uuidv4();

    // 2. Header Injection
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "X-Request-Id": requestId,
        ...(options.headers as Record<string, string>),
    };

    // If body is FormData, remove Content-Type to let browser set it with boundary
    if (options.body instanceof FormData && headers["Content-Type"] === "application/json") {
        delete headers["Content-Type"];
    }

    const sessionId = AuthStore.getSessionId();
    if (sessionId) {
        headers["X-Session-ID"] = sessionId;
    }

    // Development Logging
    if (import.meta.env.DEV) {
        console.log(`[API] ${options.method || "GET"} ${path}`, {
            requestId,
            headers,
        });
    }

    try {
        const response = await fetch(url, {
            ...options,
            headers,
        });

        // Parse JSON response
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            // If 401/403 but not JSON, it might be a server/infra error, but we treat 401 as auth error generally.
            if (response.status === 401) {
                handleSessionExpired();
                throw new Error("Unauthorized");
            }
            throw new Error(`Expected JSON response from API, got ${contentType}`);
        }

        const json: ApiResponse<T> = await response.json();

        // Development Response Logging
        if (import.meta.env.DEV) {
            console.log(`[API] Response for ${path}`, {
                requestId,
                success: json.success,
                status: response.status,
            });
        }

        // Success Case
        if (json.success) {
            return json.data;
        }

        // 3. Reactive Error Handling (Standard Envelope)
        if (json.error) {
            // Check for critical auth errors
            if (AUTH_ERROR_CODES.includes(json.error.code)) {
                handleSessionExpired(json.error.message || "Session expired.");
                throw new Error(json.error.message); // Stop processing
            }

            // Bubble up other API errors
            throw new ApiException(
                json.error.message,
                json.error.code,
                json.meta,
                json.error.details,
                response.status
            );
        }

        // Fallback for weird shapes
        throw new ApiException(
            "Unknown API error occurred",
            "UNKNOWN_ERROR",
            null,
            null,
            response.status
        );

    } catch (error) {
        // Re-throw ApiException as-is
        if (error instanceof ApiException) {
            throw error;
        }

        // Log unexpected errors
        console.error(`[API] Request failed: ${path}`, error);

        // If we already handled logic (like redirect), we might just want to stop here.
        // But the caller expects a Promise<T>, so re-throwing is appropriate.
        throw error;
    }
}

/**
 * Convenience methods
 */
export const apiClient = {
    get: <T>(path: string, options?: RequestInit) =>
        apiFetch<T>(path, { ...options, method: "GET" }),

    post: <T>(path: string, body?: any, options?: RequestInit) =>
        apiFetch<T>(path, {
            ...options,
            method: "POST",
            body: body instanceof FormData ? body : (body ? JSON.stringify(body) : undefined),
        }),

    put: <T>(path: string, body?: any, options?: RequestInit) =>
        apiFetch<T>(path, {
            ...options,
            method: "PUT",
            body: body instanceof FormData ? body : (body ? JSON.stringify(body) : undefined),
        }),

    delete: <T>(path: string, options?: RequestInit) =>
        apiFetch<T>(path, { ...options, method: "DELETE" }),

    patch: <T>(path: string, body?: any, options?: RequestInit) =>
        apiFetch<T>(path, {
            ...options,
            method: "PATCH",
            body: body instanceof FormData ? body : (body ? JSON.stringify(body) : undefined),
        }),
};
